import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle, Color, FontSize } from "@tiptap/extension-text-style";

function normalizeHtml(html) {
  const s = String(html ?? "").trim();
  if (!s) return "";
  if (s === "<p></p>" || s === "<p><br></p>" || s === '<p><br class="ProseMirror-trailingBreak"></p>') {
    return "";
  }
  return s;
}

function ToolbarButton({ active, onClick, children, disabled = false }) {
  return (
    <button
      type="button"
      className={`legal-editor-toolbar-btn ${active ? "active" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export default function LegalRichEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      FontSize,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: value || "",
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "legal-editor-content",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const next = value ?? "";
    const cur = editor.getHTML();
    if (normalizeHtml(cur) === normalizeHtml(next)) return;
    editor.commands.setContent(next || "", { emitUpdate: false });
  }, [editor, value]);

  if (!editor) return null;

  const setFontSize = (size) => {
    if (!size) {
      editor.chain().focus().unsetFontSize?.().run?.();
      return;
    }

    editor.chain().focus().setFontSize(size).run();
  };

  return (
    <div className="legal-editor">
      <div className="legal-editor-toolbar">
        <div className="legal-editor-toolbar-group">
          <ToolbarButton
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <strong>B</strong>
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <em>I</em>
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <u>U</u>
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <s>S</s>
          </ToolbarButton>
        </div>

        <div className="legal-editor-toolbar-group">
          <select
            className="legal-editor-select"
            defaultValue=""
            onChange={(e) => {
              const value = e.target.value;

              if (value === "p") {
                editor.chain().focus().setParagraph().run();
                return;
              }

              if (value === "h1") {
                editor.chain().focus().toggleHeading({ level: 1 }).run();
                return;
              }

              if (value === "h2") {
                editor.chain().focus().toggleHeading({ level: 2 }).run();
                return;
              }

              if (value === "h3") {
                editor.chain().focus().toggleHeading({ level: 3 }).run();
              }
            }}
          >
            <option value="">Formato</option>
            <option value="p">Párrafo</option>
            <option value="h1">Título 1</option>
            <option value="h2">Título 2</option>
            <option value="h3">Título 3</option>
          </select>

          <select
            className="legal-editor-select"
            defaultValue="16px"
            onChange={(e) => setFontSize(e.target.value)}
          >
            <option value="12px">12</option>
            <option value="14px">14</option>
            <option value="16px">16</option>
            <option value="18px">18</option>
            <option value="20px">20</option>
            <option value="24px">24</option>
            <option value="28px">28</option>
            <option value="32px">32</option>
          </select>

          <input
            type="color"
            className="legal-editor-color"
            title="Color de texto"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
        </div>

        <div className="legal-editor-toolbar-group">
          <ToolbarButton
            active={editor.isActive({ textAlign: "left" })}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            Izq
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive({ textAlign: "center" })}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            Cen
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive({ textAlign: "right" })}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            Der
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            • Lista
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            1. Lista
          </ToolbarButton>
        </div>

        <div className="legal-editor-toolbar-group">
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            ↺
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            ↻
          </ToolbarButton>
        </div>
      </div>

      <div className="legal-editor-sheet">
        <EditorContent editor={editor} />
        {!normalizeHtml(value) && (
          <div className="legal-editor-placeholder">
            {placeholder || "Escribe aquí el contenido del documento..."}
          </div>
        )}
      </div>
    </div>
  );
}